import React, { useEffect, useState } from 'react';
import { Modal, Form, Button, Alert, Spinner, Badge } from 'react-bootstrap';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { assignTool } from '../../../services/toolAssignmentService';
import { getUsersByRole } from '../../../services/userService';
import { listProjects } from '../../../services/projectService';
import { PROJECT_STATUS } from '../../../utils/constants';

const ToolAssignModal = ({ show, onHide, tool }) => {
  const queryClient = useQueryClient();
  const [holderId, setHolderId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (show) {
      setHolderId(''); setProjectId(''); setNotes(''); setError('');
    }
  }, [show]);

  const { data: techniciansData } = useQuery({
    queryKey: ['technicians-list'],
    queryFn: () => getUsersByRole('technician'),
    enabled: show,
    staleTime: 5 * 60 * 1000
  });

  const { data: projectsData } = useQuery({
    queryKey: ['projects-active-list'],
    // Issue #296: 'active' no longer exists in the live status set; the
    // in-execution status is now 'in_progress'.
    queryFn: () => listProjects({ status: PROJECT_STATUS.IN_PROGRESS, pageSize: 100 }),
    enabled: show,
    staleTime: 5 * 60 * 1000
  });

  const mutation = useMutation({
    mutationFn: () => assignTool({
      tool_id: tool?.id,
      holder_id: holderId,
      project_id: projectId || null,
      notes: notes || null
    }),
    onSuccess: ({ error: err }) => {
      if (err) { setError(err.message); return; }
      toast.success(`"${tool?.name}" asignada.`);
      queryClient.invalidateQueries({ queryKey: ['tools'] });
      queryClient.invalidateQueries({ queryKey: ['all-tools'] });
      queryClient.invalidateQueries({ queryKey: ['active-assignments'] });
      queryClient.invalidateQueries({ queryKey: ['tools-available'] });
      onHide();
    },
    onError: (e) => setError(e.message)
  });

  if (!tool) return null;
  const technicians = techniciansData?.data || techniciansData || [];
  const projects = projectsData?.data || [];

  return (
    <Modal show={show} onHide={onHide} centered fullscreen="sm-down">
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="bi bi-person-check me-2" />Asignar herramienta
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        {tool.status !== 'available' && (
          <Alert variant="warning" className="py-2">
            Esta herramienta está en estado <Badge bg="secondary">{tool.status}</Badge>. Verifica que sea correcto asignarla.
          </Alert>
        )}
        <div className="mb-3 p-2 bg-light rounded">
          <strong>{tool.name}</strong>{' '}
          <small className="text-muted font-monospace">({tool.serial_number})</small>
          {tool.brand && <div className="small text-muted">{tool.brand}{tool.model ? ` — ${tool.model}` : ''}</div>}
        </div>
        <Form.Group className="mb-3">
          <Form.Label>Técnico <span className="text-danger">*</span></Form.Label>
          <Form.Select
            value={holderId}
            onChange={e => setHolderId(e.target.value)}
            disabled={mutation.isPending}
          >
            <option value="">Seleccionar técnico...</option>
            {technicians.map(u => (
              <option key={u.id} value={u.id}>{u.full_name}</option>
            ))}
          </Form.Select>
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Proyecto</Form.Label>
          <Form.Select
            value={projectId}
            onChange={e => setProjectId(e.target.value)}
            disabled={mutation.isPending}
          >
            <option value="">Sin proyecto</option>
            {projects.map(p => (
              <option key={p.id} value={p.id}>
                {p.project_number ? `${p.project_number} — ` : ''}{p.title}
              </option>
            ))}
          </Form.Select>
          <Form.Text className="text-muted">
            Opcional. Asociar la asignación a un proyecto facilita reportes de costo.
          </Form.Text>
        </Form.Group>
        <Form.Group>
          <Form.Label>Notas</Form.Label>
          <Form.Control
            as="textarea" rows={2}
            value={notes} onChange={e => setNotes(e.target.value)}
            placeholder="Propósito, condición al salir, fecha tentativa de retorno..."
            disabled={mutation.isPending}
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={mutation.isPending}>
          Cancelar
        </Button>
        <Button
          variant="primary"
          disabled={!holderId || mutation.isPending}
          onClick={() => { setError(''); mutation.mutate(); }}
        >
          {mutation.isPending && <Spinner size="sm" animation="border" className="me-1" />}
          Confirmar asignación
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ToolAssignModal;
